import {
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';

import { ContentManagementService } from 'src/app/Services/content-management/content-management.service';
import { FlashCardManagementService } from 'src/app/Services/flashcard-management.service';
import { ConstantVars } from 'src/app/utils/constants/constants';
import { ExamManagementService } from 'src/app/Services/exam-management/exam-management.service';

export interface IStudyUnitDisplayOrder {
  Study_Unit_Id: number;
  Path_Name: string;
  Study_Unit_Name: string;
  Study_Unit_Status_Type_Code: string;
  Study_unit_Display_Order: number;
  Major_Topic_ID: number;
  Major_Topic_Name: string;
  Major_Topic_Sort_Order: number;
}

@Component({
  selector: 'app-weekly-practice-test-study-unit-selection',
  templateUrl: './weekly-practice-test-study-unit-selection.component.html',
  styleUrls: ['./weekly-practice-test-study-unit-selection.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class WeeklyPracticeTestStudyUnitSelectionComponent implements OnInit {
  @Input() SubjectId!: number;
  @Input() courseId!: number;
  @Input() weeklyPracticeTestId!: number;
  @Input() practiceTestSequenceNumber!: number;
  @Input() ExamAgencyName!: string;
  @Input() CourseName!: string;
  @Input() ExamYear!: string;
  @Input() totalQuestions!: number;
  @Input() subjectName!: string;

  path = '';
  selectedStudyUnitIds: number[] = [];
  groupedStudyUnits: {
    majorTopicId: number;
    majorTopicName: string;
    majorTopicSortOrder: number;
    formArray: FormArray;
    items: IStudyUnitDisplayOrder[];
  }[] = [];

  @ViewChild('scrollArea', { static: true })
  scrollArea!: ElementRef<HTMLElement>;

  constructor(
    private fb: FormBuilder,
    public activeModal: NgbActiveModal,
    private service: ContentManagementService,
    private toastr: ToastrService,
    private flashcardService: FlashCardManagementService,
    private examService: ExamManagementService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.loadPath();
    this.getSelectedStudyUnits();
    this.getStudyUnitSubject();
  }

  loadPath() {
    const payload = {
      SubjectId: this.SubjectId,
      ScreenName: 'CONTENT_MANAGEMENT',
    };

    this.flashcardService
      .getPath$(payload)
      .pipe(catchError(() => of('')))
      .subscribe((res) => (this.path = res));
  }

  getSelectedStudyUnits() {
    const payload = {
      CourseTestID: this.weeklyPracticeTestId,

    };

    console.log('🔍 Fetching selected study units with payload:', payload);

    this.examService.getWeeklyPracticeTestStudyUnit(payload).subscribe(
      (res) => {
        console.log('✅ Selected Study Units Response:', res);
        if (Array.isArray(res)) {
          // Only include study units where Selected_Ind is 'Y'
          this.selectedStudyUnitIds = res
            .filter((item: any) => item.Selected_Ind === 'Y')
            .map((item: any) => item.Study_Unit_Id);
          console.log('📌 Selected IDs:', this.selectedStudyUnitIds);
        }
      },
      (error) => {
        console.error('⚠️ Error fetching selected study units:', error);
      }
    );
  }

  getStudyUnitSubject() {
    const payload = {
      Subject_ID: +this.SubjectId,
      Course_Id: +this.courseId,
    };

    console.log('🔍 API Payload:', payload);

    this.service.getStudyUnitsDisplayOrder(payload).subscribe(
      (res) => {
        console.log('✅ API Response:', res);

        if (!Array.isArray(res)) {
          console.warn('⚠️ Response is not an array:', res);
          return;
        }

        console.log('📊 Total items received:', res.length);

        const groupMap = new Map<number, IStudyUnitDisplayOrder[]>();

        res.forEach((item) => {
          if (!groupMap.has(item.Major_Topic_ID)) {
            groupMap.set(item.Major_Topic_ID, []);
          }
          groupMap.get(item.Major_Topic_ID)!.push(item);
        });

        this.groupedStudyUnits = [];

        groupMap.forEach((items, majorTopicId) => {
          const sortedItems = [...items].sort(
            (a, b) =>
              (a.Study_unit_Display_Order ?? 0) -
              (b.Study_unit_Display_Order ?? 0)
          );

          const formArray = this.fb.array(
            sortedItems.map((e) => this.createFormGroup(e))
          );

          this.groupedStudyUnits.push({
            majorTopicId,
            majorTopicName: sortedItems[0].Major_Topic_Name,
            majorTopicSortOrder: sortedItems[0].Major_Topic_Sort_Order,
            formArray,
            items: sortedItems,
          });
        });

        console.log('🎯 Grouped Study Units:', this.groupedStudyUnits);

        // ✅ SORT GROUPS
        this.groupedStudyUnits.sort(
          (a, b) => a.majorTopicSortOrder - b.majorTopicSortOrder
        );
      },
      (error) => {
        console.error('❌ API Error:', error);
        this.toastr.error('Failed to load study units', '', {
          timeOut: ConstantVars.ToastErrorTimeout,
        });
      }
    );
  }

  createFormGroup(value: IStudyUnitDisplayOrder): FormGroup {
    const isSelected = this.selectedStudyUnitIds.includes(value.Study_Unit_Id);
    return this.fb.group({
      studyUnitId: [{ value: value.Study_Unit_Id, disabled: true }],
      studyUnitName: [{ value: value.Study_Unit_Name, disabled: true }],
      topic: [{ value: value.Path_Name, disabled: true }],
      selected: [isSelected],
    });
  }

  onSave() {
    const selectedStudyUnits = this.groupedStudyUnits.reduce((acc, group) => [
      ...acc,
      ...group.items
        .map((item, index) => ({
          item,
          isSelected: group.formArray.at(index)?.get('selected')?.value,
        }))
        .filter((e) => e.isSelected)
        .map((e) => ({
          Study_Unit_Id: e.item.Study_Unit_Id,
          Study_Unit_Name: e.item.Study_Unit_Name,
          Path_Name: e.item.Path_Name,
        }))
    ], [] as any[]);

    if (selectedStudyUnits.length === 0) {
      this.toastr.warning(
        'Please select at least one study unit',
        '',
        { timeOut: ConstantVars.ToastSucessTimeout }
      );
      return;
    }

    const payload = {
      CourseTestId: this.weeklyPracticeTestId,
      UDTPracticeTestStudyUnit: selectedStudyUnits.map((unit) => ({
        StudyUnitID: unit.Study_Unit_Id,
        SelectedIND: 'Y',
      })),
    };

    console.log('💾 Saving selected study units:', payload);

    this.examService.insertUpdateWeeklyPracticeTestStudyUnit(payload).subscribe(
      (res) => {
        console.log('✅ Save successful:', res);
        this.toastr.success(
          'Study Units Saved Successfully',
          '',
          { timeOut: ConstantVars.ToastSucessTimeout }
        );

        // Close modal and navigate to question selection screen
        this.activeModal.close(selectedStudyUnits);
        this.ensureCurrentRouteHasSelectedSubjectId().then(() => {
          this.router.navigate(['Exam/WeeklyPracticeTestQuestionSelection', this.weeklyPracticeTestId], {
            queryParams: {
              c_id: this.courseId,
              eA_Name: this.ExamAgencyName,
              c_Name: this.CourseName,
              eY_Name: this.ExamYear,
              s_id: this.SubjectId,
              s_name: this.subjectName,
              t_q: this.totalQuestions,
            },
          });
        });
      },
      (error) => {
        console.error('❌ Save error:', error);
        this.toastr.error(
          'Failed to save study units',
          '',
          { timeOut: ConstantVars.ToastErrorTimeout }
        );
      }
    );
  }

  private ensureCurrentRouteHasSelectedSubjectId(): Promise<boolean> {
    const currentUrlTree = this.router.parseUrl(this.router.url);
    const hasSelectedSubjectId = !!currentUrlTree.queryParams['subject_id'];

    if (hasSelectedSubjectId) {
      return Promise.resolve(true);
    }

    return this.router.navigate([], {
      queryParams: { subject_id: this.SubjectId },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  onCross() {
    this.activeModal.close(false);
  }
}
